import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';


import { updateRoom, getRoomsByProjectId, getFloorsBySiteId } from "../../api/locations";
import { API_URL } from "../../config/api";
import { createProjectSite, updateProjectSite } from "../../api/projects";
import { autocompleteAddress, getPlaceDetails, GoogleAddressSchema } from "../../api/address";
import { CreateRoomModal } from "../CreateRoomModal";
import ConfirmationDialog from "../ConfirmationDialog";

import { 
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaCircle,
  FaMapMarkerAlt,
  FaSave
} from 'react-icons/fa';

import {
  getTableHeaderStyle,
  getFormSelectContainerStyle,
  getFormSelectStyle,
  getMultilineFieldStyle,
  DesignSystem
} from '../../styles/designSystem';
import { LoadingSpinner } from '../LoadingSpinner';

interface ProjectLocationsProps {
  projectData: any;
  projectId: string;
  onProjectUpdate?: () => Promise<void>;
  readOnly?: boolean;
  archived?: boolean;
  preloadedRooms?: any[];
}

interface AddressSuggestion {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface LocationRowData {
  id: string;
  name: string;
  room_identifier?: string;
  site_id: string;
  floor_id: string;
  directions?: string;
  access_requirements?: string;
  access_contacts?: string; // Keep for backward compatibility
  access_contacts_name?: string;
  access_contacts_surname?: string;
  contact_phone?: string;
  contact_email?: string;
  site?: {
    id: string;
    name: string;
    address: string;
  };
  floor?: {
    id: string;
    name: string;
  };
  racks?: any[];
}

// Location row component
const LocationRow = ({
  location,
  projectId: _projectId,
  onSubmit,
  onDelete,
  isNew = false,
  readOnly = false
}: {
  location: LocationRowData,
  projectId: string,
  onSubmit: Function,
  onDelete: (locationId: string) => void,
  isNew?: boolean,
  readOnly?: boolean
}) => {
  const [form, setForm] = useState({ ...location });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [phoneError, setPhoneError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');

  useEffect(() => {
    setForm({ ...location });
  }, [location]);

  const getStatusColor = (hasAccess: boolean) => {
    return hasAccess ? DesignSystem.colors.status.completed : DesignSystem.colors.status.notStarted;
  };

  // Function to save changes
  // Validate phone number format
  const validatePhone = (phone: string): boolean => {
    if (!phone || phone.trim() === '') {
      return true; // Empty is valid (optional field)
    }
    const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
    // International format: + followed by 1-3 digit country code and 4-14 digits
    const internationalPhoneRegex = /^\+[1-9]\d{7,14}$/;
    return internationalPhoneRegex.test(cleanPhone);
  };

  const saveChanges = async (updatedForm: any) => {
    setIsSaving(true);
    try {
      await onSubmit(updatedForm);
    } finally {
      setIsSaving(false);
    }
  };
  const autoSaveFields = [
    'access_contacts_name',
    'access_contacts_surname',
    'access_requirements',
    'name',
    'contact_phone',
    'contact_email',
    'room_identifier'
  ];
  const handleFieldChange = (field: string, value: any) => {
    // Sanitize phone number - remove "tel:" prefix if present
    let sanitizedValue = value;
    if (field === 'contact_phone' && typeof value === 'string') {
      sanitizedValue = value.replace(/^tel:/i, '').trim();
    }

    // Just update the form state, don't validate or save yet
    setForm(prev => ({ ...prev, [field]: sanitizedValue }));

    // Auto-save for certain fields immediately (but NOT contact_phone - wait for blur)
    if (autoSaveFields.includes(field)) {
      const updatedForm = { ...form, [field]: sanitizedValue };
      saveChanges(updatedForm);
    }
  };

  // Function to handle text field blur - only save specific fields
  const handleFieldBlur = (field: string, value: any) => {
    // Only auto-save for specific critical fields
    // For other fields (like access_requirements, directions), changes are saved manually or on row collapse
    
    if (!autoSaveFields.includes(field)) {
      return; // Don't auto-save this field
    }
    
    // Sanitize phone number - remove "tel:" prefix if present
    let sanitizedValue = value;
    if (field === 'contact_phone' && typeof value === 'string') {
      sanitizedValue = value.replace(/^tel:/i, '').trim();
      
      // Validate phone number before saving
      if (sanitizedValue && !validatePhone(sanitizedValue)) {
        setPhoneError('Invalid phone format. Use international format (e.g., +1234567890)');
        console.warn('Invalid phone number format, not saving:', sanitizedValue);
        return; // Don't save if phone is invalid
      } else {
        setPhoneError('');
      }
    }
    
    // Validate email before saving
    if (field === 'contact_email') {
      if (sanitizedValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitizedValue)) {
        const errorMsg = 'Invalid email format';
        setEmailError(errorMsg);
        toast.error('Please enter a valid email address');
        console.warn('Invalid email format, not saving:', sanitizedValue);
        return; // Don't save invalid email
      } else {
        setEmailError('');
      }
    }
    
    const updatedForm = { ...form, [field]: sanitizedValue };
    setForm(updatedForm);
    
    // Check if value actually changed
    const originalValue = location[field as keyof LocationRowData];
    let normalizedOriginal = originalValue;
    let normalizedNew = sanitizedValue;
    
    if (field === 'contact_phone') {
      normalizedOriginal = typeof originalValue === 'string' ? originalValue.replace(/^tel:/i, '').trim() : originalValue;
    }
    
    // Only save if the field actually changed
    if (normalizedOriginal !== normalizedNew) {
      console.log(`🔄 Field ${field} changed, saving:`, { from: normalizedOriginal, to: normalizedNew });
      saveChanges(updatedForm);
    } else {
      console.log(`⏭️ Field ${field} unchanged, skipping save`);
    }
  };

  const hasAccessInfo = !!(form.access_requirements || form.access_contacts || form.access_contacts_name || form.access_contacts_surname || form.contact_phone || form.contact_email);

  return (
    <div className={`group hover:bg-[#fafafa]/50 transition-all duration-200 ${isNew ? 'bg-[#f5f5f5]/30' : ''}`}>
      {/* Main Row */}
      <div className="flex items-start px-6 py-4 relative" style={{ borderBottom: '0.5px solid #eaeaea' }}>
        {/* Clickable Area for Expand */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        
        {/* Expand Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="mr-4 p-1 rounded-md hover:bg-[#fafafa] transition-colors relative z-10 flex items-center"
        >
          <FaChevronDown className={`w-3 h-3 text-[#aaa] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Access Status Indicator */}
        <div className="mr-4 relative z-10 flex items-start pt-1">
          <div className="relative">
            {/* Rotating Ring around status dot - when saving any field */}
            {isSaving && (
              <div className={`absolute inset-0 w-5 h-5 -ml-1.5 -mt-1.5 rounded-full border-2 border-transparent animate-spin ${
                hasAccessInfo ? 'border-t-green-500/60 border-r-green-500/60' : 'border-t-gray-300/60 border-r-gray-300/60'
              }`} style={{ animation: 'spin 3s linear infinite' }} />
            )}
            {/* Status Dot */}
            <FaCircle 
              className="w-2 h-2" 
              style={{ color: getStatusColor(hasAccessInfo) }}
            />
          </div>
        </div>

        {/* Room Name */}
        <div className="flex-1 min-w-0 relative z-10 flex items-start">
          <input
            type="text"
            value={form.name || ''}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            onBlur={(e) => handleFieldBlur('name', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none focus:ring-0 placeholder-[#aaa] font-medium text-[#171717] text-base"
            placeholder="Room name..."
            style={{
              fontSize: '15px',
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontWeight: 360,
            }}
          />
        </div>

        {/* Site */}
        <div className="mx-6 w-32 relative z-10 flex items-start">
          {form.site?.name ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Scroll to Sites section
                const sitesSection = document.getElementById('sites-section');
                if (sitesSection) {
                  sitesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  // Optional: highlight the section briefly
                  sitesSection.classList.add('ring-2', 'ring-[#888]', 'ring-offset-2');
                  setTimeout(() => {
                    sitesSection.classList.remove('ring-2', 'ring-[#888]', 'ring-offset-2');
                  }, 2000);
                }
              }}
              className="text-left w-full text-sm text-[#171717] hover:text-[#444] hover:underline transition-colors"
              style={getMultilineFieldStyle()}
              title="Click to view site details"
            >
              {form.site.name}
            </button>
          ) : (
            <span className="text-sm text-[#aaa]" style={getMultilineFieldStyle()}>
              No site
            </span>
          )}
        </div>

        {/* Floor */}
        <div className="mx-6 w-24 relative z-10 flex items-start">
          <div className="text-sm text-[#444]">
            {form.floor?.name || 'No floor'}
          </div>
        </div>

        {/* Contact Info - Expanded width */}
        <div className="mx-6 w-40 relative z-10 flex items-start">
          <div className="text-sm text-[#444]">
            {(form.access_contacts_name || form.access_contacts_surname) && (
              <div className="truncate font-medium">
                {`${form.access_contacts_name || ''} ${form.access_contacts_surname || ''}`.trim()}
              </div>
            )}
            {form.contact_phone && (
              <div className="truncate text-xs">{form.contact_phone}</div>
            )}
            {form.contact_email && (
              <div className="truncate text-xs text-[#888]">{form.contact_email}</div>
            )}
            {!form.access_contacts_name && !form.access_contacts_surname && !form.contact_phone && !form.contact_email && (
              <span className="text-[#aaa]">No contact</span>
            )}
          </div>
        </div>

        {/* Racks Count */}
        <div className="mx-6 w-20 relative z-10 flex items-start">
          <div className="text-sm text-[#444] text-center">
            {form.racks?.length || 0}
          </div>
        </div>

        {/* Address */}
        <div className="mx-6 w-48 relative z-10 flex items-start">
          <textarea
            value={form.site?.address || ''}
            onChange={() => {
              // Since address is read-only, we don't really change it
              // but we still need the onChange to avoid React warnings
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none placeholder-[#aaa] resize-none"
            style={getMultilineFieldStyle()}
            placeholder="No address"
            rows={3}
            readOnly
          />
        </div>

        {/* Actions */}
        <div className="ml-6 w-32 relative z-10 flex items-start space-x-2">
          <button
            disabled
            className="flex items-center space-x-1 px-3 py-1.5 bg-[#bbb] text-white rounded-md cursor-not-allowed text-xs font-medium"
            title="Coming soon"
            onClick={(e) => e.stopPropagation()}
          >
            <span>Open</span>
          </button>
          {!readOnly && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(form.id);
              }}
              className="flex items-center space-x-1 px-3 py-1.5 bg-[#dc2626] text-white rounded-md hover:bg-red-700 transition-colors text-xs font-medium"
              title="Delete Room"
            >
              <span>Delete</span>
            </button>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 py-4 bg-[#fafafa]/30" style={{ borderBottom: '0.5px solid #eaeaea' }}>
          <div className="grid grid-cols-12 gap-4">
            {/* Access Requirements */}
            <div className="col-span-6">
              <label className="block text-xs font-medium text-[#888] mb-1">Access Requirements</label>
              <textarea
                value={form.access_requirements || ''}
                onChange={(e) => setForm(prev => ({ ...prev, access_requirements: e.target.value }))}
                onBlur={(e) => handleFieldBlur('access_requirements', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 resize-none"
                placeholder="Access requirements..."
              />
            </div>

            {/* Access Contacts */}
            <div className="col-span-4">
              <label className="block text-xs font-medium text-[#888] mb-1">Access Contacts</label>
              
              {/* Name and Surname inputs */}
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  value={form.access_contacts_name || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, access_contacts_name: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('access_contacts_name', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                  placeholder="First name..."
                />
                <input
                  type="text"
                  value={form.access_contacts_surname || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, access_contacts_surname: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('access_contacts_surname', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
                  placeholder="Last name..."
                />
              </div>
              
              <div className="space-y-1">
                <input
                  type="tel"
                  value={form.contact_phone || ''}
                  onChange={(e) => handleFieldChange('contact_phone', e.target.value)}
                  onBlur={(e) => handleFieldBlur('contact_phone', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 ${
                    phoneError ? 'border-red-500' : 'border-[#eaeaea]'
                  }`}
                  placeholder="Phone number (e.g., +1234567890)..."
                />
                {phoneError && (
                  <p className="text-red-500 text-xs mt-1">{phoneError}</p>
                )}
              </div>
              
              <div className="space-y-1">
                <input
                  type="email"
                  value={form.contact_email || ''}
                  onChange={(e) => handleFieldChange('contact_email', e.target.value)}
                  onBlur={(e) => handleFieldBlur('contact_email', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 ${
                    emailError ? 'border-red-500' : 'border-[#eaeaea]'
                  }`}
                  placeholder="Email address..."
                />
                {emailError && (
                  <p className="text-red-500 text-xs mt-1">{emailError}</p>
                )}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export const ProjectLocations = ({ projectData, projectId, onProjectUpdate, readOnly = false, archived = false, preloadedRooms }: ProjectLocationsProps) => {
  const { keycloak: { token } } = useKeycloak();
  const projectDataRef = useRef(projectData);
  const floorsCache = useRef<Map<string, any[]>>(new Map());
  const [locations, setLocations] = useState<LocationRowData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Update ref when projectData changes
  useEffect(() => {
    projectDataRef.current = projectData;
  }, [projectData]);
  
  // Clear floors cache when project changes
  useEffect(() => {
    floorsCache.current.clear();
  }, [projectId]);
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [siteFilter, setSiteFilter] = useState('all');
  const [floorFilter, setFloorFilter] = useState('all');
  const [pageSize, setPageSize] = useState(25);
  
  // Modals
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<LocationRowData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sites/Addresses management
  const [sites, setSites] = useState<any[]>([]);
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editingSiteName, setEditingSiteName] = useState('');
  const [editingSiteAddress, setEditingSiteAddress] = useState('');
  const [isSavingSite, setIsSavingSite] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [isLoadingAddressSuggestions, setIsLoadingAddressSuggestions] = useState(false);
  const [selectedAddressData, setSelectedAddressData] = useState<GoogleAddressSchema | null>(null);
  
  // Refs
  const addressInputRef = useRef<HTMLInputElement>(null);
  const addressSuggestionsRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    const handleScroll = () => {
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load locations from API instead of project data
  const loadLocations = useCallback(async () => {
    if (!projectId || !token) return;
    
    setIsLoading(true);
    try {
      // Use ref to avoid dependency on projectData
      const currentProjectData = projectDataRef.current;

      // Create site address lookup from project data
      const siteAddressMap = new Map();
      if (currentProjectData.sites) {
        currentProjectData.sites.forEach((site: any) => {
          const siteId = site.site_id || site.id;
          const address = site.google_address_data?.formatted_address || site.address || '';
          siteAddressMap.set(siteId, address);
        });
      }

      // Create site and floor lookup maps
      const siteMap = new Map();
      const floorMap = new Map();

      // Floors already come back with their rooms (incl. rows/racks) embedded,
      // so aggregate rooms from there instead of making a separate rooms
      // request. Only fall back to the dedicated rooms endpoint if a floor is
      // missing the `rooms` field entirely (vs. a legitimate empty array).
      const roomsFromFloors: any[] = [];
      let allFloorsHaveRoomsField = true;

      if (currentProjectData.sites) {
        // Load floors for each site to get proper floor names (with caching)
        for (const site of currentProjectData.sites) {
          const siteId = site.site_id || site.id;
          siteMap.set(siteId, site);

          // Check cache first
          let floorsData = floorsCache.current.get(siteId);

          if (!floorsData) {
            try {
              // Load floors for this site to get accurate floor names
              console.log(`Loading floors for site ${siteId}...`);
              floorsData = await getFloorsBySiteId(token, projectId, siteId, { archived });
              console.log(`Loaded ${floorsData.length} floors for site ${siteId}:`, floorsData);

              // Cache the floors
              floorsCache.current.set(siteId, floorsData);
            } catch (error) {
              console.warn(`Failed to load floors for site ${siteId}:`, error);
              // Fallback to floors from project data if available
              if (site.floors && Array.isArray(site.floors)) {
                floorsData = site.floors;
              }
              // Cache fallback data only if we have valid data
              if (floorsData && Array.isArray(floorsData)) {
                floorsCache.current.set(siteId, floorsData);
              }
            }
          } else {
            console.log(`Using cached floors for site ${siteId}:`, floorsData?.length || 0);
          }

          // Add floors to floorMap, and aggregate their embedded rooms
          if (floorsData && Array.isArray(floorsData)) {
            floorsData.forEach((floor: any) => {
              floorMap.set(floor.id, floor);
              if (Array.isArray(floor.rooms)) {
                roomsFromFloors.push(...floor.rooms);
              } else {
                allFloorsHaveRoomsField = false;
              }
            });
          } else {
            allFloorsHaveRoomsField = false;
          }
        }
      }

      let roomsData: any[];
      if (preloadedRooms) {
        console.log('ProjectLocations: Using preloaded rooms data');
        roomsData = preloadedRooms;
      } else if (allFloorsHaveRoomsField) {
        console.log('ProjectLocations: Using rooms embedded in floor responses, skipping separate rooms request:', roomsFromFloors);
        roomsData = roomsFromFloors;
      } else {
        console.log('ProjectLocations: Floors missing embedded room data, fetching rooms separately');
        roomsData = await getRoomsByProjectId(token, projectId, { archived });
        console.log('ProjectLocations: Loaded rooms from API:', roomsData);
      }

      const transformedLocations: LocationRowData[] = roomsData.map((room: any) => {
        const site = siteMap.get(room.site_id);
        const floor = floorMap.get(room.floor_id);
        const siteAddress = siteAddressMap.get(room.site_id) || '';
        
        return {
          id: room.id || '',
          name: room.name,
          room_identifier: room.room_identifier || '',
          site_id: room.site_id,
          floor_id: room.floor_id,
          directions: room.directions || '',
          access_requirements: room.access_requirements || '',
          access_contacts: room.access_contacts || '', // Keep for backward compatibility
          access_contacts_name: room.access_contacts_name || '',
          access_contacts_surname: room.access_contacts_surname || '',
          contact_phone: (room.contact_phone || '').replace(/^tel:/i, '').trim(),
          contact_email: room.contact_email || '',
          site: {
            id: room.site_id,
            name: site?.name || 'Unknown Site',
            address: siteAddress
          },
          floor: {
            id: room.floor_id,
            name: floor?.name || 'Unknown Floor'
          },
          racks: room.rows?.flatMap((row: any) => row.racks || []) || []
        };
      });
      setLocations(transformedLocations);
    } catch (error) {
      console.error('Error loading locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setIsLoading(false);
    }
    // token is a deprecated no-op arg here (see src/api/locations.ts _token params);
    // authenticatedFetch handles auth internally, so token rotation must not
    // re-trigger this callback (it did, causing duplicate room/floor fetches).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Guard against React StrictMode's dev-only mount/cleanup/remount double-invoke,
  // which would otherwise fire loadLocations() (and its N per-site floor requests)
  // twice every time this tab mounts. Must compare the *identity* of
  // loadLocations, not just "have we ever run" — this component does not
  // remount when switching projects while already on the Locations tab (only
  // its projectId prop changes), so a plain run-once ref would incorrectly
  // suppress the legitimate reload for the newly selected project too.
  const lastRanForRef = useRef<typeof loadLocations | null>(null);
  useEffect(() => {
    if (lastRanForRef.current === loadLocations) return;
    lastRanForRef.current = loadLocations;
    loadLocations();
  }, [loadLocations]);

  // Listen for room creation events to refresh locations list
  useEffect(() => {
    const handleRoomCreated = () => {
      console.log('🏠 Room created event received in ProjectLocations, refreshing locations...');
      loadLocations();
    };

    window.addEventListener('roomCreated', handleRoomCreated);
    
    return () => {
      window.removeEventListener('roomCreated', handleRoomCreated);
    };
  }, [loadLocations]);

  // Initialize sites from project data
  useEffect(() => {
    if (projectData.sites && projectData.sites.length > 0) {
      const hasLocalDefaultSite = sites.some(site => site.is_default === true);
      if (hasLocalDefaultSite) {
        return;
      }
      setSites(projectData.sites);
    }
  }, [projectData.sites]);

  // Address autocomplete with debouncing
  useEffect(() => {
    if (!editingSiteId) return;

    const delayDebounceFn = setTimeout(async () => {
      if (editingSiteAddress.length >= 3 && token) {
        setIsLoadingAddressSuggestions(true);
        try {
          const result = await autocompleteAddress(token, editingSiteAddress);
          setAddressSuggestions(result.predictions || []);
          setShowAddressSuggestions(true);
        } catch (error) {
          console.error('Error fetching address suggestions:', error);
          setAddressSuggestions([]);
        } finally {
          setIsLoadingAddressSuggestions(false);
        }
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [editingSiteAddress, token, editingSiteId]);

  // Handle click outside address suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressSuggestionsRef.current && !addressSuggestionsRef.current.contains(event.target as Node) &&
          addressInputRef.current && !addressInputRef.current.contains(event.target as Node)) {
        setShowAddressSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter locations based on search and filters
  const filteredLocations = useMemo(() => {
    return locations.filter(location => {
      // Search filter
      const matchesSearch = !searchQuery || 
        location.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.room_identifier?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.site?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.floor?.name?.toLowerCase().includes(searchQuery.toLowerCase());

      // Site filter
      const matchesSite = siteFilter === 'all' || location.site?.name === siteFilter;

      // Floor filter
      const matchesFloor = floorFilter === 'all' || location.floor?.name === floorFilter;

      return matchesSearch && matchesSite && matchesFloor;
    }).slice(0, pageSize);
  }, [locations, searchQuery, siteFilter, floorFilter, pageSize]);

  // Validation functions
  const isValidEmail = (email: string): boolean => {
    if (!email || email.trim() === '') return true; // Empty is valid (optional field)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone: string): boolean => {
    if (!phone || phone.trim() === '') return true; // Empty is valid (optional field)
    // Allow various phone formats: +1234567890, (123) 456-7890, 123-456-7890, etc.
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$|^[\+]?[(]?[\d\s\-\(\)]{10,20}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  };

  // Update location handler
  const updateLocationHandler = async (form: LocationRowData) => {
    if (!token || !projectId) return;

    try {
      // Find original location data to get required fields
      const originalLocation = locations.find(l => l.id === form.id);
      if (!originalLocation) {
        toast.error('Cannot update: location data not found. Please refresh the page.');
        return;
      }

      // Validate email and phone before sending (silent validation - toast shown in handleFieldBlur)
      if (form.contact_email && !isValidEmail(form.contact_email)) {
        console.warn('Invalid email format, skipping save:', form.contact_email);
        return;
      }

      if (form.contact_phone && !isValidPhone(form.contact_phone)) {
        console.warn('Invalid phone format, skipping save:', form.contact_phone);
        return;
      }

      // Get required fields with proper fallbacks
      const siteId = originalLocation.site_id || originalLocation.site?.id;
      const floorId = originalLocation.floor_id || originalLocation.floor?.id;
      // Use form.name if provided (user edited), otherwise use original
      const roomName = form.name || originalLocation.name;
      
      // SCRUM-243: Enhanced logging for room name updates
      console.log('🏷️ Room name update details:', {
        formName: form.name,
        originalName: originalLocation.name,
        finalRoomName: roomName,
        nameWillChange: form.name !== originalLocation.name
      });

      if (!siteId || !floorId || !roomName) {
        console.error('🚨 Cannot get required fields from original location:', {
          siteId,
          floorId,
          roomName,
          originalLocation
        });
        toast.error('Cannot update: missing location identifiers. Please refresh the page.');
        return;
      }

      // Create complete update data with all required fields
      const updateData: any = {
        // Required fields - site_id and floor_id from original, name from form (allows editing)
        site_id: siteId,
        floor_id: floorId,
        name: roomName,
        // Optional fields from form (user edits only)
        directions: form.directions,
        access_requirements: form.access_requirements,
        access_contacts: form.access_contacts, // Keep for backward compatibility
        access_contacts_name: form.access_contacts_name,
        access_contacts_surname: form.access_contacts_surname,
        project_id: projectId
      };

      // Only include contact fields if they have valid values
      if (form.contact_phone && form.contact_phone.trim() !== '') {
        updateData.contact_phone = form.contact_phone;
      }
      
      if (form.contact_email && form.contact_email.trim() !== '' && isValidEmail(form.contact_email)) {
        updateData.contact_email = form.contact_email;
      }

      console.log('🏠 updateLocationHandler - form data:', JSON.stringify(form, null, 2));
      console.log('🏠 updateLocationHandler - sending updateData:', JSON.stringify(updateData, null, 2));

      // SCRUM-243 FIX: Use updateRoom instead of updateRoomAccess to properly update room name
      // updateRoomAccess may not update name field on backend
      const updatedRoom = await updateRoom(token, projectId, form.id, updateData);
      
      console.log('✅ Room updated successfully. Response:', updatedRoom);
      console.log('🏷️ Updated room name:', updatedRoom?.name);
      
      // Update local state with only the fields we updated (preserve existing structure)
      setLocations(prev => prev.map(l => {
        if (l.id === form.id) {
          return {
            ...l,
            name: updateData.name,
            directions: updateData.directions,
            access_requirements: updateData.access_requirements,
            access_contacts: updateData.access_contacts,
            access_contacts_name: updateData.access_contacts_name,
            access_contacts_surname: updateData.access_contacts_surname,
            contact_phone: updateData.contact_phone,
            contact_email: updateData.contact_email
          };
        }
        return l;
      }));
      
      // Dispatch event to update sidebar menu
      window.dispatchEvent(new CustomEvent('roomUpdated'));
      
      toast.success(`Room "${updateData.name}" updated successfully`);
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Failed to update location');
    }
  };

  // Delete location handler - show confirmation dialog
  const handleDeleteLocation = (locationId: string) => {
    const location = locations.find(l => l.id === locationId);
    if (!location) {
      toast.error('Location not found');
      return;
    }
    
    setLocationToDelete(location);
    setShowDeleteConfirmation(true);
  };

  // Actual delete function after confirmation
  const confirmDeleteLocation = async () => {
    if (!token || !projectId || !locationToDelete) return;

    try {
      setIsDeleting(true);
      console.log('🗑️ Deleting location:', locationToDelete.id);
      
      // Call delete API
      const response = await fetch(`${API_URL}/data-center/projects/${projectId}/rooms/${locationToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'authorization': 'Bearer ' + token
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('✅ Room deleted successfully, reloading locations from API...');
      
      // Reload locations from API to ensure consistency
      await loadLocations();
      
      toast.success('Location deleted successfully');
      
      // Dispatch event for other components to react
      window.dispatchEvent(new CustomEvent('roomDeleted', { 
        detail: { roomId: locationToDelete.id, projectId } 
      }));
      
      // Close dialog
      setShowDeleteConfirmation(false);
      setLocationToDelete(null);
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error('Failed to delete location');
    } finally {
      setIsDeleting(false);
    }
  };

  const getAccessCount = () => {
    return locations.filter(l => !!(l.access_requirements || l.access_contacts || l.access_contacts_name || l.access_contacts_surname || l.contact_phone || l.contact_email)).length;
  };

  // Get unique sites and floors for filters
  const uniqueSites = useMemo(() => {
    const sites = locations.map(l => l.site?.name).filter(Boolean);
    return [...new Set(sites)] as string[];
  }, [locations]);

  const uniqueFloors = useMemo(() => {
    const floors = locations.map(l => l.floor?.name).filter(Boolean);
    return [...new Set(floors)] as string[];
  }, [locations]);

  // Site management functions
  const handleAddressSelect = async (suggestion: AddressSuggestion) => {
    if (!token) return;

    setIsLoadingAddressSuggestions(true);
    try {
      const placeDetails = await getPlaceDetails(token, suggestion.place_id);
      setSelectedAddressData(placeDetails);
      setEditingSiteAddress(placeDetails.formatted_address);
      setShowAddressSuggestions(false);
    } catch (error) {
      console.error('Error fetching place details:', error);
      toast.error('Failed to load address details');
    } finally {
      setIsLoadingAddressSuggestions(false);
    }
  };

  const handleEditSite = (site: any) => {
    setEditingSiteId(site.id);
    setEditingSiteName(site.name || '');
    setEditingSiteAddress(site.google_address_data?.formatted_address || site.address || '');
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
    setSelectedAddressData(null);
  };

  const handleAddSite = () => {
    const newSiteId = `new-site-${Date.now()}`;
    setEditingSiteId(newSiteId);
    setEditingSiteName('');
    setEditingSiteAddress('');
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
    setSelectedAddressData(null);
    
    setSites(prev => [...prev, {
      id: newSiteId,
      name: '',
      address: '',
      google_address_data: null
    }]);
  };

  const handleCancelEditSite = () => {
    if (editingSiteId?.startsWith('new-site-')) {
      setSites(prev => prev.filter(site => site.id !== editingSiteId));
    }
    setEditingSiteId(null);
    setEditingSiteName('');
    setEditingSiteAddress('');
    setSelectedAddressData(null);
  };

  const handleSaveSite = async () => {
    if (!token || !projectId || !editingSiteName.trim()) {
      toast.error('Please provide a site name');
      return;
    }

    setIsSavingSite(true);
    try {
      const isNewSite = editingSiteId?.startsWith('new-site-');
      
      if (isNewSite) {
        const newSiteData: any = {
          name: editingSiteName.trim(),
          address: editingSiteAddress.trim(),
          google_address_data: selectedAddressData || undefined,
          project_id: projectId,
          geo_location: selectedAddressData?.latitude && selectedAddressData?.longitude 
            ? [selectedAddressData.latitude, selectedAddressData.longitude]
            : [0, 0]
        };
        
        const createdSite = await createProjectSite(token, projectId, newSiteData);
        setSites(prev => prev.map(site => 
          site.id === editingSiteId ? createdSite : site
        ));
        
        // SCRUM-241 FIX: Notify parent component to refresh project data
        // This ensures the new site appears in Settings tab without page reload
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
        
        toast.success('Site created successfully');
      } else {
        const currentSite = sites.find(s => s.id === editingSiteId);
        const updatedSiteData: any = {
          ...currentSite,
          name: editingSiteName.trim(),
          address: editingSiteAddress.trim(),
          google_address_data: selectedAddressData || currentSite?.google_address_data || undefined,
          project_id: projectId,
          geo_location: selectedAddressData?.latitude && selectedAddressData?.longitude 
            ? [selectedAddressData.latitude, selectedAddressData.longitude]
            : currentSite?.geo_location || [0, 0]
        };
        
        await updateProjectSite(token, projectId, editingSiteId!, updatedSiteData);
        setSites(prev => prev.map(site => 
          site.id === editingSiteId 
            ? { ...site, ...updatedSiteData }
            : site
        ));
        
        // SCRUM-241: Notify parent component to refresh project data after update
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
        
        toast.success('Site updated successfully');
      }
      
      setEditingSiteId(null);
      setEditingSiteName('');
      setEditingSiteAddress('');
      setSelectedAddressData(null);
      
      // Reload locations to reflect address changes
      await loadLocations();
    } catch (error) {
      console.error('Error saving site:', error);
      toast.error('Failed to save site');
    } finally {
      setIsSavingSite(false);
    }
  };

  const makeDefaultSite = async (siteId: string) => {
    if (!token || !projectId) return;

    try {
      const site = sites.find(s => s.id === siteId);
      if (!site) return;

      await updateProjectSite(token, projectId, siteId, {
        ...site,
        is_default: true
      });

      setSites(prev => prev.map(s => ({
        ...s,
        is_default: s.id === siteId
      })));

      // The API may auto-resolve the project timezone from the new default site,
      // so reload the project to pick that up.
      if (onProjectUpdate) {
        await onProjectUpdate();
      }

      toast.success('Default site updated');
    } catch (error) {
      console.error('Error setting default site:', error);
      toast.error('Failed to set default site');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-shadow duration-200" style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #eaeaea' }}>
        <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#aaa] w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search locations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2.5 w-80 text-sm bg-[#fafafa] border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 transition-all"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-[#888]">
                      {filteredLocations.length} of {locations.length} locations
                    </span>
                    <div className="w-px h-4 bg-[#ddd]"></div>
                    <span className="text-[#888]">
                      {getAccessCount()} with access info
                    </span>
          </div>
            </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* Filters */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      showFilters
                        ? 'bg-[#171717] text-white border border-[#171717]'
                        : 'bg-white text-[#444] hover:bg-[#fafafa] border border-[#eaeaea]'
                    }`}
                  >
                    <FaFilter className="w-3.5 h-3.5" />
                    <span>Filter</span>
                    {(siteFilter !== 'all' || floorFilter !== 'all') && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-white text-[#171717] rounded-full font-semibold">
                        {[siteFilter, floorFilter].filter(f => f !== 'all').length}
                      </span>
                    )}
                  </button>

                  {/* Add Room */}
            {!readOnly && (
              <button
                      onClick={() => setShowAddLocationModal(true)}
                      className="flex items-center space-x-2 bg-[#171717] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#333] transition-colors"
              >
                      <FaPlus className="w-3.5 h-3.5" />
                      <span>Add Room</span>
              </button>
            )}
          </div>
        </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="mt-4 pt-4" style={{ borderTop: '0.5px solid #eaeaea' }}>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Sites</option>
                        {uniqueSites.map((site) => (
                          <option key={site} value={site}>
                            {site}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={floorFilter}
                        onChange={(e) => setFloorFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Floors</option>
                        {uniqueFloors.map((floor) => (
                          <option key={floor} value={floor}>
                            {floor}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        style={getFormSelectStyle()}
                      >
                        <option value={25}>Show 25</option>
                        <option value={50}>Show 50</option>
                        <option value={100}>Show 100</option>
                        <option value={200}>Show All</option>
                      </select>
                    </div>
      </div>

                  {/* Active Filters & Clear */}
                  {(siteFilter !== 'all' || floorFilter !== 'all') && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-[#888]">Active filters:</span>
                        {siteFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#171717] rounded-md">
                            Site: {siteFilter}
                            <button onClick={() => setSiteFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                          </span>
                        )}
                        {floorFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#171717] rounded-md">
                            Floor: {floorFilter}
                            <button onClick={() => setFloorFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                            </span>
                          )}
                      </div>
                      <button
                        onClick={() => {
                          setSiteFilter('all');
                          setFloorFilter('all');
                        }}
                        className="text-xs text-[#888] hover:text-[#444] font-medium"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Table Header */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="w-8"></div> {/* Expand button space */}
                <div className="w-4 mr-4"></div> {/* Status indicator space */}
                <div className="flex-1" role="columnheader">Room Name</div>
                <div className="mx-6 w-32" role="columnheader">Site</div>
                <div className="mx-6 w-24" role="columnheader">Floor</div>
                <div className="mx-6 w-40" role="columnheader">Contact</div>
                <div className="mx-6 w-20" role="columnheader">Racks</div>
                <div className="mx-6 w-48" role="columnheader">Address</div>
                <div className="ml-6 w-20" role="columnheader">Actions</div>
              </div>
            </div>
          </div>
                  </div>
      </div>

      {/* Location List - Scrollable Content */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        <div className="bg-white">
          {/* Loading State */}
          {isLoading && <LoadingSpinner text="Loading locations..." />}

          {/* Location Rows */}
          {!isLoading && (
            <div>
              {filteredLocations.map((location) => (
                <LocationRow
                  key={location.id}
                  location={location}
                  projectId={projectId}
                  onSubmit={updateLocationHandler}
                  onDelete={handleDeleteLocation}
                  readOnly={readOnly}
                />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && filteredLocations.length === 0 && (
            <div className="text-center py-16">
              <div className="text-[#aaa] mb-4">
                <FaMapMarkerAlt className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-light text-[#171717] mb-2">No rooms found</h3>
              <p className="text-[#555] font-light mb-8">
                {searchQuery || siteFilter !== 'all' || floorFilter !== 'all'
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by adding your first room.'
                }
          </p>
          {!readOnly && (
            <button
                  onClick={() => setShowAddLocationModal(true)}
                  className="bg-white border border-[#eaeaea] text-[#555] px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#fafafa] hover:border-[#eaeaea] transition-colors"
            >
                  Add First Room
            </button>
          )}
                  </div>
          )}
        </div>
      </div>

      {/* Addresses Section */}
      <div id="sites-section" className="max-w-[1280px] mx-auto px-0 mt-8 mb-8">
        <div className="bg-white rounded-xl p-6" style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FaMapMarkerAlt className="text-green-600 w-5 h-5" />
                <h3 className="text-lg font-semibold">Sites</h3>
              </div>

              {!readOnly && (
                <button
                  onClick={handleAddSite}
                  disabled={isSavingSite}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium bg-[#171717] text-white rounded-lg hover:bg-[#333] transition-colors disabled:opacity-50"
                >
                  <FaPlus className="w-4 h-4" />
                  <span>Add Site</span>
                </button>
              )}
            </div>
            
            {/* Sites Content - List */}
            <div className="space-y-4">
              {sites.map((site, index) => (
                <div key={site.id} className="bg-[#fafafa] border border-[#eaeaea] rounded-lg p-4">
                  {editingSiteId === site.id ? (
                    // Edit Mode
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#444] mb-2">
                          Site Name
                        </label>
                        <input
                          type="text"
                          value={editingSiteName}
                          onChange={(e) => setEditingSiteName(e.target.value)}
                          placeholder="Enter site name..."
                          className="w-full p-3 border border-[#eaeaea] rounded-lg focus:border-[#888] focus:ring-0 transition-all"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[#444] mb-2">
                          Address
                        </label>
                        <div className="relative">
                          <input
                            ref={addressInputRef}
                            type="text"
                            value={editingSiteAddress}
                            onChange={(e) => {
                              setEditingSiteAddress(e.target.value);
                              if (selectedAddressData && selectedAddressData.formatted_address !== e.target.value) {
                                setSelectedAddressData(null);
                              }
                            }}
                            placeholder="Enter site address..."
                            className="w-full p-3 border border-[#eaeaea] rounded-lg focus:border-[#888] focus:ring-0 transition-all"
                          />
                          
                          {/* Loading indicator */}
                          {isLoadingAddressSuggestions && (
                            <div className="absolute right-3 top-3">
                              <div className="animate-spin rounded-full h-4 w-4 border border-[#eaeaea] border-t-[#171717]"></div>
                            </div>
                          )}

                          {/* Address suggestions dropdown */}
                          {showAddressSuggestions && addressSuggestions.length > 0 && (
                            <div 
                              ref={addressSuggestionsRef}
                              className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#eaeaea] rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] z-50 max-h-60 overflow-y-auto"
                            >
                              {addressSuggestions.map((suggestion, index) => (
                                <button
                                  key={suggestion.place_id || index}
                                  onClick={() => handleAddressSelect(suggestion)}
                                  className="w-full px-4 py-3 text-left hover:bg-[#fafafa] focus:bg-[#fafafa] focus:outline-none border-b border-[#f5f5f5] last:border-b-0"
                                >
                                  <div className="flex items-start space-x-3">
                                    <svg className="w-4 h-4 text-[#aaa] mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-[#171717]">
                                        {suggestion.structured_formatting.main_text}
                                      </p>
                                      <p className="text-xs text-[#888]">
                                        {suggestion.structured_formatting.secondary_text}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={handleCancelEditSite}
                          disabled={isSavingSite}
                          className="px-4 py-2 text-[#555] bg-[#f5f5f5] rounded-lg hover:bg-[#eaeaea] transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveSite}
                          disabled={isSavingSite || !editingSiteName.trim()}
                          className="px-4 py-2 bg-[#171717] text-white rounded-lg hover:bg-[#333] transition-colors disabled:opacity-50 flex items-center space-x-2"
                        >
                          {isSavingSite ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent"></div>
                              <span>Saving...</span>
                            </>
                          ) : (
                            <>
                              <FaSave className="w-4 h-4" />
                              <span>Save</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <FaMapMarkerAlt className="text-[#aaa] w-4 h-4 mt-1" />
                        <div className="flex-1">
                          {/* Site Name */}
                          <div className="mb-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#f5f5f5] text-[#171717]">
                              {site.name || `Site ${index + 1}`}
                            </span>
                            {site.is_default && (
                              <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">Default</span>
                            )}
                            {!site.is_default && (
                              <button
                                onClick={() => makeDefaultSite(site.id)}
                                className="ml-2 px-2 py-0.5 bg-[#f5f5f5] text-[#555] text-xs rounded hover:bg-yellow-100 hover:text-yellow-800"
                              >
                                Make Default
                              </button>
                            )}
                          </div>
                          {/* Address */}
                          <p className="text-[#171717] font-medium" style={{
                            fontSize: '13px',
                            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                            color: '#444',
                            lineHeight: '1.3'
                          }}>
                            {site.google_address_data?.formatted_address || site.address || 'No address specified'}
                          </p>
                        </div>
                      </div>

                      {!readOnly && (
                        <button
                          onClick={() => handleEditSite(site)}
                          className="flex items-center space-x-2 px-3 py-2 text-sm font-medium bg-[#fafafa] text-[#555] rounded-lg hover:bg-[#fafafa] transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Edit</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {sites.length === 0 && (
                <div className="bg-[#fafafa] border border-[#eaeaea] rounded-lg p-8 text-center">
                  <FaMapMarkerAlt className="text-[#aaa] w-8 h-8 mx-auto mb-3" />
                  <p className="text-[#888] mb-4">No sites added yet</p>
                  {!readOnly && (
                    <button
                      onClick={handleAddSite}
                      disabled={isSavingSite}
                      className="px-4 py-2 bg-[#171717] text-white rounded-lg hover:bg-[#333] transition-colors disabled:opacity-50 flex items-center space-x-2 mx-auto"
                    >
                      <FaPlus className="w-4 h-4" />
                      <span>Add First Site</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Room Modal */}
      {showAddLocationModal && (
        <CreateRoomModal 
          onClose={() => {
            setShowAddLocationModal(false);
            // Reload locations after creating a new one
            loadLocations();
          }}
          projectId={projectId}
          title="Add Room"
        />
      )}

      {/* Delete Location Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setLocationToDelete(null);
        }}
        onConfirm={confirmDeleteLocation}
        title="⚠️ Delete Location - DANGER ZONE"
        message={`You are about to permanently delete location "${locationToDelete?.name}". This action will destroy ALL location data and cannot be undone.`}
        confirmText="Delete Location Forever"
        cancelText="Cancel"
        confirmButtonClass="bg-[#dc2626] hover:bg-[#b91c1c]"
        requireTextConfirmation={true}
        textToConfirm={locationToDelete?.name || ''}
        warningItems={[
          "Location access requirements and contacts",
          "All room configuration data",
          "Associated racks and equipment", 
          "Historical access logs",
          "This action cannot be undone"
        ]}
        isLoading={isDeleting}
      />
    </div>
  );
}; 